import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  ProviderToken,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { connectState } from '@examdojo/angular/util';
import { getAnimationAppearDisappear } from '@examdojo/animation';
import { SyllabusQuery, SyllabusService } from '@examdojo/category';
import { ImageChatRealtimeService, provideImageChat } from '@examdojo/chat';
import { ButtonComponent } from '@examdojo/core/button';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { IconComponent } from '@examdojo/core/icon';
import { ToastService } from '@examdojo/core/toast';
import { assertNonNullable } from '@examdojo/core/util/assert';
import { isNotNullish } from '@examdojo/core/util/nullish';
import { QuestionStatus } from '@examdojo/models/question';
import {
  ChecklistConfig,
  countMarkschemes,
  countTotalImages,
  extractImageInfo,
  extractMarkschemeInfo,
  extractMCQInfo,
  extractTableInfo,
  JsonEditorTriggerDirective,
  NewQuestionItemButtonComponent,
  provideQuestionItemsForm,
  QuestionActiveItemEditorComponent,
  QuestionEntityQuery,
  QuestionEntityService,
  QuestionEntityStore,
  QuestionItemsEditorComponent,
  QuestionStatusSelectorComponent,
} from '@examdojo/question';
import { ReferenceQuestionPopupTriggerDirective, ReferenceQuestionService } from '@examdojo/reference-question';
import {
  ConsensusFeedbackFormComponent,
  createDynamicSurveyConfig,
  DerivativeQuestionHistoryDialogService,
  DerivativeQuestionReviewType,
  QUESTION_VALIDATION_STATUS_STRATEGY_MAPPING,
  QuestionReviewFeedbackFormState,
  SubmitButtonText,
} from '@examdojo/review';
import baseConfig from '@settings/ibdp-derivative-question-checklist.json';
import { AngularSplitModule } from 'angular-split';
import { distinctUntilChanged, EMPTY, filter, finalize, map, merge, skip, switchMap, tap } from 'rxjs';
import { CmsUrlParts } from '../cms.model';
import { DetailViewWrapperComponent } from '../detail-view/wrapper/detail-view-wrapper.component';
import { QuestionEditorImageChatEffectService } from './effects/question-editor-image-chat-effect.service';
import { QuestionEditorItemsFormService } from './question-items-form/question-editor-items-form-service';
import { QuestionEditorService } from './question-items-form/question-editor.service';

@Component({
  selector: 'exam-question-editor-container',
  templateUrl: './question-editor-container.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'h-full' },
  imports: [
    QuestionItemsEditorComponent,
    QuestionActiveItemEditorComponent,
    DetailViewWrapperComponent,
    ButtonComponent,
    AngularSplitModule,
    NewQuestionItemButtonComponent,
    JsonEditorTriggerDirective,
    IconComponent,
    MatTooltipModule,
    QuestionStatusSelectorComponent,
    RouterLink,
    ReferenceQuestionPopupTriggerDirective,
    MatButtonToggleModule,
    ConsensusFeedbackFormComponent,
  ],
  providers: [
    QuestionEditorService,
    provideQuestionItemsForm(QuestionEditorItemsFormService),
    provideImageChat(),
    QuestionEditorImageChatEffectService,
  ],
  animations: [getAnimationAppearDisappear()],
})
export class QuestionEditorContainerComponent {
  constructor(
    private readonly questionEntityQuery: QuestionEntityQuery,
    private readonly questionEditorItemsFormService: QuestionEditorItemsFormService,
    private readonly questionEditorService: QuestionEditorService,
    private readonly destroyRef: DestroyRef,
    private readonly activatedRoute: ActivatedRoute,
    private readonly questionEntityStore: QuestionEntityStore,
    private readonly questionEntityService: QuestionEntityService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly toastService: ToastService,
    private readonly referenceQuestionService: ReferenceQuestionService,
    private readonly syllabusQuery: SyllabusQuery,
    private readonly syllabusService: SyllabusService,
    private readonly router: Router,
    private readonly derivativeQuestionHistoryDialogService: DerivativeQuestionHistoryDialogService,
  ) {
    merge(this.saveQuestionItemsOnFormChange(), this.updateActiveSyllabusOnReferenceQuestionChange())
      .pipe(takeUntilDestroyed())
      .subscribe();

    effect(() => {
      const activeId = this.id();
      if (!activeId) {
        return;
      }

      this.questionEntityStore.setActive(Number(activeId));
    });
  }

  readonly id = input<string | undefined>(undefined);

  private readonly instantiatedServices = [QuestionEditorImageChatEffectService, ImageChatRealtimeService].map(
    (service: ProviderToken<unknown>) => inject(service),
  );

  readonly CmsUrlParts = CmsUrlParts;
  private readonly initialized$ = this.questionEditorService.activeQuestionFetched$;

  readonly questionItemsForm = this.questionEditorItemsFormService.form;

  readonly isSaving = signal<boolean>(false);

  readonly isCreatingSeedQuestion = signal<boolean>(false);

  readonly referenceQuestion$ = this.questionEntityQuery.selectActive().pipe(
    filter(isNotNullish),
    distinctUntilChanged(),
    switchMap((question) => this.referenceQuestionService.fetchByQuestionId(question.id)),
    this.errorHandlerService.catchHttpErrors(() => EMPTY, { showErrorDetailsInToast: true }),
  );

  readonly question = toSignal(this.questionEntityQuery.selectActive());

  readonly state = connectState({
    question: this.questionEntityQuery.selectActive(),
    referenceQuestion: this.referenceQuestion$,
    initialized: this.initialized$,
    isLoading: this.questionEntityQuery.selectLoading(),
    canUndo: this.questionEditorItemsFormService.canUndo$,
    canRedo: this.questionEditorItemsFormService.canRedo$,
    calculatorAllowed: this.questionEntityQuery
      .selectActive()
      .pipe(map((question) => question?.question.metadata?.calculator_allowed ?? false)),
    markCalculationMethod: this.syllabusQuery.markCalculationMethod$,
    activeSyllabus: this.syllabusQuery.selectActive(),
  });

  readonly activeQuestionItem = this.questionEditorItemsFormService.activeQuestionItem;

  readonly statusCtrl = this.questionEditorService.questionForm.controls.status;

  readonly checklistConfig = baseConfig as ChecklistConfig;
  readonly feedbackFormComponent = viewChild<ConsensusFeedbackFormComponent>(ConsensusFeedbackFormComponent);
  readonly sidebarTab = signal<'details' | 'feedback'>('details');
  readonly feedbackFormState = signal<QuestionReviewFeedbackFormState | null>(null);

  readonly isSeedQuestion = computed(() => {
    const status = this.question()?.question.status;
    return status === 'ACTIVE_SEED' || status === 'INACTIVE_SEED';
  });

  readonly totalImages = computed(() => countTotalImages(this.question()?.question.question_items ?? []));
  readonly totalMarkschemes = computed(() => countMarkschemes(this.question()?.question.question_items ?? []));
  readonly imageInfo = computed(() => extractImageInfo(this.question()?.question.question_items ?? []));
  readonly markschemeInfo = computed(() => extractMarkschemeInfo(this.question()?.question.question_items ?? []));
  readonly mcqInfo = computed(() => extractMCQInfo(this.question()?.question.question_items ?? []));
  readonly tableInfo = computed(() => extractTableInfo(this.question()?.question.question_items ?? []));

  readonly dynamicSurveyConfig = computed(() => {
    return createDynamicSurveyConfig(
      this.checklistConfig as ChecklistConfig<QuestionStatus>,
      this.markschemeInfo(),
      this.imageInfo(),
      this.mcqInfo(),
      this.tableInfo(),
      QUESTION_VALIDATION_STATUS_STRATEGY_MAPPING as Record<DerivativeQuestionReviewType, QuestionStatus>,
      DerivativeQuestionReviewType.HumanReview,
    );
  });

  readonly SubmitButtonText = SubmitButtonText;
  readonly DerivativeQuestionReviewType = DerivativeQuestionReviewType;

  undo() {
    this.questionEditorItemsFormService.undo();
  }

  redo() {
    this.questionEditorItemsFormService.redo();
  }

  saveQuestionItems() {
    this.isSaving.set(true);

    this.questionEditorItemsFormService
      .getProcessedFormData()
      .pipe(
        switchMap((processedData) => {
          assertNonNullable(this.state.question?.id, 'questionId');

          if (this.isSeedQuestion()) {
            const feedback = this.feedbackFormState()?.value?.feedback;
            const isValid = this.feedbackFormState()?.isValid;

            if (!feedback || !isValid) {
              this.toastService.error('Feedback response is required for seed questions');
              return EMPTY;
            }

            const currentQuestion = this.question();
            const currentMetadata = currentQuestion?.question.metadata || {};
            const currentSeed = currentMetadata.seed || {};

            const updatedMetadata = {
              ...currentMetadata,
              seed: {
                ...currentSeed,
                expected_feedback: feedback,
                initial_status:
                  currentSeed.initial_status ??
                  QUESTION_VALIDATION_STATUS_STRATEGY_MAPPING[DerivativeQuestionReviewType.HumanReview],
              },
            };

            return this.questionEntityService.update(this.state.question.id, {
              question_items: processedData,
              metadata: updatedMetadata,
            });
          }

          return this.questionEntityService.update(this.state.question.id, {
            question_items: processedData,
          });
        }),
        tap(() => {
          this.questionItemsForm.markAsPristine();
          this.questionEditorItemsFormService.setActiveQuestionItem(null);

          this.toastService.success(
            this.isSeedQuestion() ? 'Question and feedback saved successfully' : 'Question Items updated',
          );
        }),
        this.errorHandlerService.catchHttpErrors(() => EMPTY, { showErrorDetailsInToast: true }),
        finalize(() => this.isSaving.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  async openHistoryDialog() {
    const questionId = this.state.question?.id;
    if (!questionId) {
      return;
    }

    await this.derivativeQuestionHistoryDialogService.openDialog({
      input: { questionId },
    });
  }

  createSeedQuestion() {
    assertNonNullable(this.state.question?.id, 'questionId');

    this.questionEntityService
      .createSeedQuestion(this.state.question.id)
      .pipe(
        switchMap((seedQuestionId) => {
          if (this.state.activeSyllabus?.slug && seedQuestionId) {
            return this.router.navigate(['../', seedQuestionId], {
              relativeTo: this.activatedRoute,
            });
          }
          return EMPTY;
        }),
        this.errorHandlerService.catchHttpErrors(() => EMPTY, { showErrorDetailsInToast: true }),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isCreatingSeedQuestion.set(false)),
      )
      .subscribe();
  }

  private saveQuestionItemsOnFormChange() {
    return this.statusCtrl.valueChanges.pipe(
      switchMap((status) => {
        assertNonNullable(this.state.question?.id, 'questionId');

        // validate feedback before allowing status change to ACTIVE_SEED
        if (status === 'ACTIVE_SEED') {
          const currentQuestion = this.question();
          const expectedFeedback = currentQuestion?.question.metadata?.seed?.expected_feedback;
          const feedbackFormState = this.feedbackFormState();

          if (!expectedFeedback || !feedbackFormState?.isValid) {
            this.toastService.error('Cannot change status to Active Seed: feedback is not valid or missing');

            const previousStatus = currentQuestion?.question.status;
            if (previousStatus) {
              this.statusCtrl.setValue(previousStatus, { emitEvent: false });
            }

            return EMPTY;
          }
        }

        return this.questionEntityService
          .update(this.state.question?.id, {
            status,
          })
          .pipe(
            tap(() => {
              this.toastService.success('Status updated');
            }),
            this.errorHandlerService.catchHttpErrors(() => EMPTY, { showErrorDetailsInToast: true }),
          );
      }),
    );
  }

  private updateActiveSyllabusOnReferenceQuestionChange() {
    return this.questionEntityQuery.selectActiveId().pipe(
      distinctUntilChanged(),
      filter(isNotNullish),
      switchMap((questionId) => {
        return this.questionEntityService.fetchSyllabusIdByQuestionId(questionId).pipe(
          tap((syllabusId) => {
            this.syllabusService.setActive(syllabusId ?? null);
          }),
          this.errorHandlerService.catchHttpErrors(() => EMPTY, { showErrorDetailsInToast: true }),
        );
      }),
      switchMap(() => {
        // then react to syllabus changes and redirect to the question list when syllabus changes
        return this.syllabusQuery.selectActive().pipe(
          skip(1),
          tap((syllabus) => {
            if (syllabus) {
              this.router.navigate([CmsUrlParts.Syllabus, syllabus.slug, CmsUrlParts.Questions]);
            }
          }),
        );
      }),
    );
  }
}
